import React from 'react';

/**
 * North Indian Diamond-Style Kundali Chart (Lagna Chart)
 * Professional Vedic astrology chart with 12 houses
 */
function LagnaChart({ kundali }) {
  if (!kundali) return null;

  const { planetary_positions, ascendant } = kundali;

  // Calculate which planets are in which house
  const planetsInHouses = {};

  Object.entries(planetary_positions || {}).forEach(([planet, data]) => {
    const planetRashi = data.rashi_num;
    const ascendantRashi = ascendant.rashi_num;

    let house = planetRashi - ascendantRashi + 1;
    if (house <= 0) house += 12;
    if (house > 12) house -= 12;

    if (!planetsInHouses[house]) planetsInHouses[house] = [];
    planetsInHouses[house].push({
      name: planet,
      abbr: getPlanetAbbr(planet),
      degree: Math.round(data.degree_in_rashi || 0),
      color: getPlanetColor(planet)
    });
  });

  function getPlanetAbbr(name) {
    const map = {
      'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
      'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa',
      'Rahu': 'Ra', 'Ketu': 'Ke'
    };
    return map[name] || name.substring(0, 2);
  }

  function getPlanetColor(name) {
    const colors = {
      'Sun': '#EF4444', 'Moon': '#60A5FA', 'Mars': '#EF4444',
      'Mercury': '#34D399', 'Jupiter': '#A78BFA', 'Venus': '#F472B6',
      'Saturn': '#9CA3AF', 'Rahu': '#FB923C', 'Ketu': '#FB923C'
    };
    return colors[name] || '#9CA3AF';
  }

  const rashiShort = [
    'Ari', 'Tau', 'Gem', 'Can', 'Leo', 'Vir',
    'Lib', 'Sco', 'Sag', 'Cap', 'Aqu', 'Pis'
  ];

  function getHouseRashi(houseNum) {
    let rashiIdx = (ascendant.rashi_num - 1 + houseNum - 1) % 12;
    return rashiShort[rashiIdx];
  }

  // SVG size
  const S = 500;
  const P = 8; // padding inside border

  // Key points
  const TL = [P, P];
  const TR = [S - P, P];
  const BL = [P, S - P];
  const BR = [S - P, S - P];
  const TM = [S / 2, P];
  const RM = [S - P, S / 2];
  const BM = [S / 2, S - P];
  const LM = [P, S / 2];
  const CT = [S / 2, S / 2];

  // Center text positions for each house (carefully placed INSIDE the house boundaries)
  const houseCenters = {
    1:  [S/2, S*0.18],          // Top center diamond
    2:  [S*0.18, S*0.09],       // Top-left triangle
    3:  [S*0.09, S*0.25],       // Left-upper triangle
    4:  [S*0.21, S/2],          // Left center diamond
    5:  [S*0.09, S*0.75],       // Left-lower triangle
    6:  [S*0.18, S*0.91],       // Bottom-left triangle
    7:  [S/2, S*0.82],          // Bottom center diamond
    8:  [S*0.82, S*0.91],       // Bottom-right triangle
    9:  [S*0.91, S*0.75],       // Right-lower triangle
    10: [S*0.79, S/2],          // Right center diamond
    11: [S*0.91, S*0.25],       // Right-upper triangle
    12: [S*0.82, S*0.09]        // Top-right triangle
  };

  // House number positions (near edges)
  const houseNumPos = {
    1:  [S/2, S*0.07],
    2:  [S*0.23, P+12],
    3:  [P+12, S*0.23],
    4:  [S*0.07, S/2],
    5:  [P+12, S*0.77],
    6:  [S*0.23, S-P-10],
    7:  [S/2, S*0.93],
    8:  [S*0.77, S-P-10],
    9:  [S-P-12, S*0.77],
    10: [S*0.93, S/2],
    11: [S-P-12, S*0.23],
    12: [S*0.77, P+12]
  };

  function toPoints(pts) {
    return pts.map(p => p.join(',')).join(' ');
  }

  // Render planets
  function renderPlanets(houseNum) {
    const planets = planetsInHouses[houseNum] || [];
    const center = houseCenters[houseNum];
    if (!planets.length) return null;

    const isDiamond = [1, 4, 7, 10].includes(houseNum);
    const fs = isDiamond ? 14 : 12;
    const lh = isDiamond ? 18 : 15;
    const startY = center[1] - ((planets.length - 1) * lh) / 2;

    return planets.map((planet, i) => (
      <text
        key={`p-${houseNum}-${i}`}
        x={center[0]}
        y={startY + i * lh}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={planet.color}
        fontSize={fs}
        fontWeight="700"
        fontFamily="'Montserrat', sans-serif"
      >
        {planet.abbr}
        <tspan fontSize={fs - 4} dy="-5" fontWeight="400">{planet.degree}</tspan>
      </text>
    ));
  }

  // Render rashi label below planets
  function renderRashi(houseNum) {
    const center = houseCenters[houseNum];
    const planets = planetsInHouses[houseNum] || [];
    const isDiamond = [1, 4, 7, 10].includes(houseNum);
    const offset = planets.length > 0 ? (isDiamond ? 30 : 18) + (planets.length - 1) * 8 : 0;
    return (
      <text
        key={`r-${houseNum}`}
        x={center[0]}
        y={center[1] + offset}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#4B5563"
        fontSize="10"
        fontFamily="'Montserrat', sans-serif"
      >
        {getHouseRashi(houseNum)}
      </text>
    );
  }

  return (
    <div data-testid="lagna-chart" style={{ width: '100%', maxWidth: '520px', margin: '0 auto' }}>
      <h3 style={{
        textAlign: 'center', fontSize: '1.25rem', fontWeight: 700,
        marginBottom: '1rem', color: '#E5E7EB'
      }}>
        Lagna Chart (Birth Chart)
      </h3>

      <div style={{
        position: 'relative', width: '100%', paddingBottom: '100%',
        background: '#0C0E1A', borderRadius: '8px', overflow: 'hidden'
      }}>
        <svg
          viewBox={`0 0 ${S} ${S}`}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        >
          {/* Dark background */}
          <rect x="0" y="0" width={S} height={S} fill="#0C0E1A" />

          {/* Single outer border */}
          <rect x={P} y={P} width={S-2*P} height={S-2*P} fill="none" stroke="#D97706" strokeWidth="2" />

          {/* Diagonal lines: corners to center */}
          <line x1={TL[0]} y1={TL[1]} x2={CT[0]} y2={CT[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={TR[0]} y1={TR[1]} x2={CT[0]} y2={CT[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={BL[0]} y1={BL[1]} x2={CT[0]} y2={CT[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={BR[0]} y1={BR[1]} x2={CT[0]} y2={CT[1]} stroke="#D97706" strokeWidth="1" />

          {/* Midpoint connecting lines */}
          <line x1={TM[0]} y1={TM[1]} x2={LM[0]} y2={LM[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={TM[0]} y1={TM[1]} x2={RM[0]} y2={RM[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={BM[0]} y1={BM[1]} x2={LM[0]} y2={LM[1]} stroke="#D97706" strokeWidth="1" />
          <line x1={BM[0]} y1={BM[1]} x2={RM[0]} y2={RM[1]} stroke="#D97706" strokeWidth="1" />

          {/* House numbers */}
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
            <text
              key={`hn-${h}`}
              x={houseNumPos[h][0]}
              y={houseNumPos[h][1]}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={h === 1 ? '#D97706' : '#374151'}
              fontSize="11"
              fontWeight="600"
              fontFamily="'Montserrat', sans-serif"
            >
              {h}
            </text>
          ))}

          {/* Ascendant label inside House 1 (top center diamond) */}
          {(() => {
            const planets1 = planetsInHouses[1] || [];
            const labelY = planets1.length > 0 ? S * 0.12 : S * 0.16;
            return (
              <>
                <text x={S/2} y={labelY} textAnchor="middle" dominantBaseline="middle"
                  fill="#D97706" fontSize="10" fontWeight="700" fontFamily="'Montserrat', sans-serif" letterSpacing="0.5">
                  {ascendant.rashi ? ascendant.rashi.split(' ')[0] : 'ASC'}
                </text>
                <text x={S/2} y={labelY + 12} textAnchor="middle" dominantBaseline="middle"
                  fill="#6B7280" fontSize="8" fontFamily="'Montserrat', sans-serif" letterSpacing="1">
                  LAGNA
                </text>
              </>
            );
          })()}

          {/* Rashi labels */}
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => renderRashi(h))}

          {/* Planets */}
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => renderPlanets(h))}
        </svg>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center',
        marginTop: '1rem', padding: '0.75rem', background: '#151827',
        borderRadius: '8px', border: '1px solid #2D3748'
      }}>
        {[
          { a: 'Su', n: 'Sun', c: '#EF4444' },
          { a: 'Mo', n: 'Moon', c: '#60A5FA' },
          { a: 'Ma', n: 'Mars', c: '#EF4444' },
          { a: 'Me', n: 'Mercury', c: '#34D399' },
          { a: 'Ju', n: 'Jupiter', c: '#A78BFA' },
          { a: 'Ve', n: 'Venus', c: '#F472B6' },
          { a: 'Sa', n: 'Saturn', c: '#9CA3AF' },
          { a: 'Ra', n: 'Rahu', c: '#FB923C' },
          { a: 'Ke', n: 'Ketu', c: '#FB923C' }
        ].map(p => (
          <span key={p.a} style={{ fontSize: '0.75rem', color: p.c, fontWeight: 600 }}>
            {p.a}={p.n}
          </span>
        ))}
      </div>
    </div>
  );
}

export default LagnaChart;
